/**
 * Manifest version detection — surfaces which build of `manifest.xml`
 * Office actually used to open this task pane, as distinct from the
 * deployment version (`ADDIN_VERSION` in Settings.tsx, read from
 * package.json at build time).
 *
 * The two can drift: a new manifest.xml can take Microsoft up to ~72h to
 * propagate to a given Office client, while the server (and the JS bundle it
 * serves) updates the moment a deploy lands. So the *deployment* version is
 * always "latest, for everyone, instantly" — it can never tell you whether a
 * particular user's Office is still holding an old manifest.
 *
 * To recover that signal, the build stamps the manifest's `<Version>` onto
 * the query string of the taskpane URLs inside manifest.xml itself (see
 * scripts/stampManifestVersion.js, wired into webpack.config.js and
 * scripts/generate-dev-manifest.js). Whatever copy of manifest.xml Office is
 * currently holding — new or stale — is what it requests the task pane
 * with, so reading this page's own query string on load recovers exactly
 * the version Office's manifest cache is stuck on.
 */

const MANIFEST_VERSION_PARAM = "manifestVersion";

/** Falls back to this when the page wasn't opened with a stamped URL, e.g. a plain browser tab during dev. */
const UNKNOWN_VERSION = "unknown";

/** Pure so it's testable without a DOM/window. */
export function parseManifestVersion(search: string): string {
  const value = new URLSearchParams(search).get(MANIFEST_VERSION_PARAM);
  return value && value.trim() !== "" ? value : UNKNOWN_VERSION;
}

/** The version Office's currently-loaded manifest requested this page with. */
export function getManifestVersion(): string {
  return parseManifestVersion(
    typeof window !== "undefined" ? window.location.search : "",
  );
}
